import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowUpRight, MapPin, Bookmark } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { FadeUp, Overline } from "@/components/Reveal";
import { SEO } from "@/lib/seo";
import { api } from "@/lib/api";
import { useSavedUniversities } from "@/lib/useSaved";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function UniversityProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { saved, toggle } = useSavedUniversities();
  const [u, setU] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setU(null);
    api.get(`/universities/${slug}`).then((r) => setU(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="pt-48 pb-32 text-center">
          <p className="font-serif text-4xl text-navy">University not found.</p>
          <button data-testid="back-to-universities" onClick={() => navigate("/universities")} className="mt-8 border border-navy px-8 py-3 text-sm font-semibold hover:bg-navy hover:text-cream transition-colors">Browse all universities</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!u) return <div className="min-h-screen bg-cream"><Navbar /></div>;

  const sections = [
    { title: "Overview", body: u.overview },
    { title: "Location", body: u.location_text },
    { title: "Global reputation", body: u.reputation },
    { title: "Entry requirements", body: u.entry_requirements },
    { title: "Estimated tuition", body: u.tuition_estimate },
    { title: "Scholarships", body: u.scholarships_text },
    { title: "Campus experience", body: u.campus_experience },
    { title: "Accommodation", body: u.accommodation },
    { title: "International student support", body: u.international_support },
  ];

  return (
    <div data-testid="university-profile-page">
      <SEO title={u.seo_title || `${u.name} | Vitaliah`} description={u.seo_description || u.descriptor} />
      <Navbar />

      <section className="relative bg-navy text-cream grain">
        <div className="absolute inset-0 overflow-hidden">
          <img src={u.image} alt={u.name} className="w-full h-full object-cover saturate-[0.7] opacity-30" />
          <div className="absolute inset-0 bg-navy/60" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-44 pb-24">
          <FadeUp>
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">
              <MapPin className="h-3.5 w-3.5" /> {u.city}, {u.state} — {u.group}
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl font-medium tracking-tight leading-[1.05] max-w-4xl">{u.name}</h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-6 text-lg text-cream/75 max-w-2xl leading-relaxed">{u.descriptor}</p>
          </FadeUp>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-8 space-y-16">
            {sections.map((s, i) => (
              <FadeUp key={s.title} delay={0.03 * i}>
                <div data-testid={`profile-section-${s.title.toLowerCase().replace(/\s+/g, "-")}`} className="border-t border-border pt-10">
                  <h2 className="font-serif text-3xl font-medium text-navy">{s.title}</h2>
                  <p className="mt-5 text-navy/70 leading-relaxed">{s.body}</p>
                </div>
              </FadeUp>
            ))}
            {u.popular_study_areas?.length > 0 && (
              <FadeUp>
                <div className="border-t border-border pt-10" data-testid="profile-study-areas">
                  <h2 className="font-serif text-3xl font-medium text-navy">Popular study areas</h2>
                  <div className="mt-6 flex flex-wrap gap-3">
                    {u.popular_study_areas.map((a) => (
                      <span key={a} className="border border-border bg-white px-5 py-2.5 text-sm text-navy/80">{a}</span>
                    ))}
                  </div>
                </div>
              </FadeUp>
            )}
            {u.faqs?.length > 0 && (
              <FadeUp>
                <div className="border-t border-border pt-10" data-testid="profile-faqs">
                  <h2 className="font-serif text-3xl font-medium text-navy">FAQs</h2>
                  <Accordion type="single" collapsible className="mt-6 space-y-3">
                    {u.faqs.map((f, i) => (
                      <AccordionItem key={i} value={`f-${i}`} className="bg-white border border-border px-6">
                        <AccordionTrigger className="text-left font-medium text-navy hover:no-underline py-5">{f.q}</AccordionTrigger>
                        <AccordionContent className="text-navy/70 leading-relaxed pb-5">{f.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </FadeUp>
            )}
          </div>
          <div className="lg:col-span-4">
            <FadeUp className="lg:sticky lg:top-28">
              <div className="bg-navy text-cream p-9 grain relative" data-testid="profile-cta-card">
                <div className="relative z-10">
                  <Overline dark>Considering {u.name.split(" ")[0] === "University" ? "this university" : u.name}?</Overline>
                  <h3 className="mt-5 font-serif text-3xl font-medium leading-snug">Why consider this university?</h3>
                  <p className="mt-4 text-sm text-cream/70 leading-relaxed">{u.why_consider}</p>
                  <button
                    data-testid="profile-guidance-cta"
                    onClick={() => navigate("/contact")}
                    className="mt-8 inline-flex w-full items-center justify-center gap-3 bg-gold text-navy px-8 py-4 text-sm font-semibold tracking-wide hover:bg-cream transition-colors duration-300"
                  >
                    Get Free Guidance <ArrowUpRight className="h-4 w-4" />
                  </button>
                  <button
                    data-testid="profile-save-university"
                    onClick={() => toggle(u.slug)}
                    className="mt-3 inline-flex w-full items-center justify-center gap-3 border border-cream/30 text-cream px-8 py-3.5 text-sm font-semibold tracking-wide hover:border-gold hover:text-gold transition-colors duration-300"
                  >
                    <Bookmark className="h-4 w-4" fill={saved.has(u.slug) ? "currentColor" : "none"} />
                    {saved.has(u.slug) ? "Saved to your profile" : "Save this university"}
                  </button>
                  <p className="mt-5 text-xs text-cream/45 leading-relaxed">
                    Vitaliah is independent and not affiliated with {u.name}. Always confirm details on the university's official website.
                  </p>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
